/**
 * Revenue Attribution — demo dataset.
 *
 * Fully deterministic: every number is derived from a fixed seed and a day index,
 * so screenshots are stable between reloads. Only the date *labels* follow the
 * current day, so the report never looks stale.
 *
 * ── How the numbers relate to each other ─────────────────────────────────────
 * Three estimators, deliberately kept separate because they are separate in the
 * real product too:
 *
 *   AI Sessions      observed   — GA4 sessions whose landing page is this page
 *                                 and whose channel resolves to AI Search.
 *   Conv             observed   — GA4 key events on those sessions. Small numbers,
 *                                 because AI traffic is ~1% of the site.
 *   $/Session        modeled    — the page's conversion rate measured across ALL
 *                                 its traffic × value per conversion. Measured on
 *                                 AI sessions alone it would be noise.
 *   Revenue          derived    — AI sessions × modeled $/Session.
 *
 * That is why a page can show a healthy $/Session with 0 observed AI conversions:
 * the rate comes from the page's full sample, the volume comes from AI. The UI
 * says so explicitly (see COPY.report.pageTable.valuePerSessionTooltip).
 *
 * The confidence threshold (30 conversions) is applied to the *full-traffic*
 * conversion count, since that is the sample the rate is estimated from.
 */

export type ChannelId = "ai" | "direct" | "organic";
export type PageTypeId = "comparison" | "pricing" | "integration" | "content" | "other";
export type TrendMetric = "sessions" | "conversions" | "revenue";

export const PAGE_TYPE_LABELS: Record<PageTypeId, string> = {
    comparison: "Comparison",
    pricing: "Pricing",
    integration: "Integration",
    content: "Content",
    other: "Other"
};

export const CHANNEL_LABELS: Record<ChannelId, string> = {
    ai: "AI Search",
    direct: "Direct",
    organic: "Organic"
};

/** Recharts stroke colors — reuse of the existing AI Visibility palette. */
export const CHANNEL_COLORS: Record<ChannelId, string> = {
    ai: "#2F54EB",
    direct: "#8C8C8C",
    organic: "#5B9A5E"
};

// ── Window ───────────────────────────────────────────────────────────────────

export const WINDOW_DAYS = 90;
/** §6/§7 — we only ever hold 90 days from GA4 on the free tier. */
export const AVAILABLE_HISTORY_DAYS = 90;
/** §6 — conversions needed before a per-page rate is trusted. */
export const CONFIDENCE_MIN_CONVERSIONS = 30;

// ── Calibration targets (§5.1, §8) ───────────────────────────────────────────

const TARGET = {
    aiSessions: 3284,
    prevAiSessions: 2329, // → +41% period over period
    aiConversions: 47,
    prevAiConversions: 37,
    aiRevenue: 376_000,
    prevAiRevenue: 287_000, // → +31%
    organicSessions: 185_098,
    prevOrganicSessions: 183_640, // flat
    directSessions: 80_607,
    prevDirectSessions: 78_920,
    /** Paid / Referral / Social / Email. Not charted, but part of the AI-share denominator. */
    otherChannelSessions: 29_556
};

/** Revenue per session by channel. AI is ~3x Organic — the headline insight. */
export const CHANNEL_VALUE_PER_SESSION: Record<ChannelId, number> = {
    ai: 114.5,
    organic: 38.2,
    direct: 46.8
};

const VALUE_PER_CONVERSION = { lead: 8000, signup: 600 } as const;

/** Site-wide fallback used for pages that fail the confidence check. */
export const SITE_AVG_VALUE_PER_SESSION = CHANNEL_VALUE_PER_SESSION.ai;

/** §8 — source mix inside the AI channel. */
export const AI_SOURCE_MIX: { name: string; share: number; color: string }[] = [
    { name: "ChatGPT", share: 0.62, color: "#2F54EB" },
    { name: "Gemini", share: 0.21, color: "#597EF7" },
    { name: "Perplexity", share: 0.11, color: "#85A5FF" },
    { name: "Claude", share: 0.04, color: "#ADC6FF" },
    { name: "Other", share: 0.02, color: "#D6E4FF" }
];

// ── Deterministic helpers ────────────────────────────────────────────────────

function mulberry32(seed: number) {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6d2b79f5) >>> 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

/**
 * Distribute `total` across `weights` as integers summing to exactly `total`.
 * Largest-remainder, so ratios are preserved and the columns always foot.
 */
function allocate(weights: number[], total: number): number[] {
    const sum = weights.reduce((acc, w) => acc + w, 0);
    if (sum <= 0) return weights.map(() => 0);
    const exact = weights.map((w) => (w / sum) * total);
    const base = exact.map((v) => Math.floor(v));
    let remaining = total - base.reduce((acc, v) => acc + v, 0);
    const order = exact
        .map((v, i) => ({ i, frac: v - Math.floor(v) }))
        .sort((a, b) => b.frac - a.frac || a.i - b.i);
    for (let k = 0; remaining > 0; k++, remaining--) base[order[k % order.length].i] += 1;
    return base;
}

/** Weekday/weekend seasonality. `dow` is 0=Sun … 6=Sat. */
function seasonality(dow: number): number {
    if (dow === 0) return 0.58;
    if (dow === 6) return 0.62;
    if (dow === 5) return 0.94;
    return 1.13;
}

// ── Page catalog ─────────────────────────────────────────────────────────────
//
// aiWeight    — share of AI-sourced sessions (AI over-indexes on comparison pages).
// allSessions — this page's total 90-day sessions across every channel. Absolute
//               rather than a share, because it decides whether the page clears the
//               30-conversion confidence bar and that needs to be legible here.
// vps         — target revenue per AI session, before calibration to the $376k total.
// conv        — which key event this page converts on, so the confidence maths is honest.
//
// Note the shape this produces: a cheap page needs far more traffic than an
// expensive one to earn a trustworthy rate. /demo clears the bar on 3.6k sessions;
// /about never clears it on 5k. That asymmetry is real, not an artefact.

type PageSeed = {
    path: string;
    title: string;
    type: PageTypeId;
    aiWeight: number;
    allSessions: number;
    vps: number;
    conv: keyof typeof VALUE_PER_CONVERSION;
};

const PAGE_SEEDS: PageSeed[] = [
    // Comparison — AI answer engines lean hard on these.
    { path: "/compare/thrivestack-vs-amplitude", title: "ThriveStack vs Amplitude", type: "comparison", aiWeight: 420, allSessions: 8300, vps: 208, conv: "lead" },
    { path: "/compare/thrivestack-vs-mixpanel", title: "ThriveStack vs Mixpanel", type: "comparison", aiWeight: 310, allSessions: 7100, vps: 185, conv: "lead" },
    { path: "/compare/thrivestack-vs-heap", title: "ThriveStack vs Heap", type: "comparison", aiWeight: 190, allSessions: 6000, vps: 168, conv: "lead" },
    { path: "/compare/thrivestack-vs-june", title: "ThriveStack vs June", type: "comparison", aiWeight: 96, allSessions: 5500, vps: 144, conv: "lead" },
    { path: "/vs/segment", title: "ThriveStack vs Segment", type: "comparison", aiWeight: 168, allSessions: 5400, vps: 176, conv: "lead" },
    { path: "/vs/posthog", title: "ThriveStack vs PostHog", type: "comparison", aiWeight: 142, allSessions: 5300, vps: 161, conv: "lead" },
    { path: "/alternatives/amplitude", title: "Amplitude alternatives", type: "comparison", aiWeight: 205, allSessions: 5900, vps: 195, conv: "lead" },
    { path: "/alternatives/mixpanel", title: "Mixpanel alternatives", type: "comparison", aiWeight: 134, allSessions: 4700, vps: 174, conv: "lead" },
    { path: "/alternatives/pendo", title: "Pendo alternatives", type: "comparison", aiWeight: 88, allSessions: 5000, vps: 148, conv: "lead" },

    // Pricing
    { path: "/pricing", title: "Pricing", type: "pricing", aiWeight: 395, allSessions: 15900, vps: 143, conv: "lead" },
    { path: "/pricing/enterprise", title: "Enterprise pricing", type: "pricing", aiWeight: 112, allSessions: 4200, vps: 227, conv: "lead" },
    { path: "/pricing/startups", title: "Startup pricing", type: "pricing", aiWeight: 74, allSessions: 3000, vps: 64, conv: "lead" }, // thin

    // Integration
    { path: "/integrations/segment", title: "Segment integration", type: "integration", aiWeight: 126, allSessions: 7900, vps: 103, conv: "lead" },
    { path: "/integrations/snowflake", title: "Snowflake integration", type: "integration", aiWeight: 98, allSessions: 6500, vps: 115, conv: "lead" },
    { path: "/integrations/hubspot", title: "HubSpot integration", type: "integration", aiWeight: 110, allSessions: 8300, vps: 95, conv: "lead" },
    { path: "/integrations/salesforce", title: "Salesforce integration", type: "integration", aiWeight: 86, allSessions: 6400, vps: 119, conv: "lead" },
    { path: "/integrations/stripe", title: "Stripe integration", type: "integration", aiWeight: 64, allSessions: 8600, vps: 86, conv: "lead" },
    { path: "/integrations/webhooks", title: "Webhooks", type: "integration", aiWeight: 41, allSessions: 3100, vps: 70, conv: "lead" }, // thin

    // Content — heavy organic traffic, cheap conversions.
    // §5.3: heavily cited, low value. This row is the reason the table exists.
    { path: "/blog/what-is-a-cdp", title: "What is a CDP?", type: "content", aiWeight: 286, allSessions: 17500, vps: 7.2, conv: "signup" },
    { path: "/blog/product-led-growth-metrics", title: "PLG metrics that matter", type: "content", aiWeight: 178, allSessions: 4100, vps: 24, conv: "signup" },
    { path: "/blog/plg-vs-sales-led", title: "PLG vs sales-led", type: "content", aiWeight: 131, allSessions: 2900, vps: 38, conv: "signup" },
    { path: "/blog/activation-rate-benchmarks", title: "Activation rate benchmarks", type: "content", aiWeight: 104, allSessions: 2600, vps: 31, conv: "signup" },
    { path: "/blog/self-serve-onboarding", title: "Self-serve onboarding", type: "content", aiWeight: 77, allSessions: 3600, vps: 19, conv: "signup" },
    { path: "/blog/free-trial-vs-freemium", title: "Free trial vs freemium", type: "content", aiWeight: 69, allSessions: 2700, vps: 27, conv: "signup" },
    { path: "/blog/north-star-metric", title: "Choosing a north star metric", type: "content", aiWeight: 58, allSessions: 4200, vps: 14, conv: "signup" },
    { path: "/blog/saas-retention-curves", title: "Reading retention curves", type: "content", aiWeight: 34, allSessions: 1800, vps: 20, conv: "signup" }, // thin
    { path: "/glossary/product-qualified-lead", title: "Product qualified lead", type: "content", aiWeight: 92, allSessions: 4000, vps: 16, conv: "signup" },
    { path: "/glossary/time-to-value", title: "Time to value", type: "content", aiWeight: 61, allSessions: 5200, vps: 11, conv: "signup" },
    { path: "/glossary/activation", title: "Activation", type: "content", aiWeight: 47, allSessions: 6200, vps: 9, conv: "signup" },
    { path: "/glossary/customer-data-platform", title: "Customer data platform", type: "content", aiWeight: 55, allSessions: 4900, vps: 12, conv: "signup" },
    { path: "/glossary/reverse-etl", title: "Reverse ETL", type: "content", aiWeight: 23, allSessions: 1500, vps: 11, conv: "signup" }, // thin

    // Other
    { path: "/", title: "Home", type: "other", aiWeight: 248, allSessions: 34000, vps: 76, conv: "lead" },
    { path: "/product/analytics", title: "Product analytics", type: "other", aiWeight: 143, allSessions: 12300, vps: 88, conv: "lead" },
    { path: "/product/attribution", title: "Attribution", type: "other", aiWeight: 118, allSessions: 9100, vps: 100, conv: "lead" },
    { path: "/demo", title: "Book a demo", type: "other", aiWeight: 96, allSessions: 3600, vps: 292, conv: "lead" },
    { path: "/customers/fivex", title: "FiveX case study", type: "other", aiWeight: 63, allSessions: 2600, vps: 64, conv: "lead" }, // thin
    { path: "/customers/orderly", title: "Orderly case study", type: "other", aiWeight: 27, allSessions: 1200, vps: 58, conv: "lead" }, // thin
    { path: "/docs/quickstart", title: "Quickstart", type: "other", aiWeight: 71, allSessions: 24000, vps: 31, conv: "lead" },
    { path: "/docs/sdk/javascript", title: "JavaScript SDK", type: "other", aiWeight: 29, allSessions: 4000, vps: 28, conv: "lead" }, // thin
    { path: "/security", title: "Security", type: "other", aiWeight: 21, allSessions: 900, vps: 92, conv: "lead" }, // thin
    { path: "/about", title: "About", type: "other", aiWeight: 38, allSessions: 5000, vps: 19, conv: "lead" } // thin
];

/** §5.3 — prompts citing each page, shown in the row expand. */
const CITED_PROMPTS: Record<string, string[]> = {
    "/compare/thrivestack-vs-amplitude": [
        "best amplitude alternative for product led growth",
        "thrivestack vs amplitude pricing",
        "is amplitude worth it for a seed stage saas",
        "amplitude competitors 2026",
        "which product analytics tool has built in attribution",
        "cheapest amplitude replacement",
        "thrivestack or amplitude for b2b saas",
        "amplitude alternatives with plg metrics"
    ],
    "/compare/thrivestack-vs-mixpanel": [
        "mixpanel vs thrivestack",
        "mixpanel alternatives for b2b saas",
        "product analytics tools compared 2026",
        "is mixpanel good for account level analytics",
        "mixpanel pricing too expensive alternatives",
        "best mixpanel competitor"
    ],
    "/compare/thrivestack-vs-heap": [
        "heap analytics alternatives",
        "heap vs thrivestack autocapture",
        "does heap support account level reporting",
        "heap competitors for plg"
    ],
    "/compare/thrivestack-vs-june": ["june.so alternatives", "june vs thrivestack", "best june alternative for b2b"],
    "/vs/segment": [
        "do i need segment if i have product analytics",
        "segment alternatives cheaper",
        "thrivestack vs segment cdp",
        "segment competitors 2026",
        "cdp vs product analytics"
    ],
    "/vs/posthog": [
        "posthog vs thrivestack",
        "posthog alternatives for b2b saas",
        "is posthog good for account based analytics",
        "posthog competitors"
    ],
    "/alternatives/amplitude": [
        "amplitude alternatives",
        "top 5 amplitude alternatives 2026",
        "amplitude replacement for startups",
        "free amplitude alternative",
        "amplitude alternatives with revenue attribution",
        "who competes with amplitude"
    ],
    "/alternatives/mixpanel": ["mixpanel alternatives 2026", "open source mixpanel alternative", "mixpanel replacement b2b"],
    "/alternatives/pendo": ["pendo alternatives", "pendo vs product analytics tools"],
    "/pricing": [
        "thrivestack pricing",
        "how much does thrivestack cost",
        "product analytics pricing comparison",
        "thrivestack free tier limits",
        "cheapest product analytics for startups",
        "thrivestack enterprise pricing",
        "does thrivestack charge per event"
    ],
    "/pricing/enterprise": ["thrivestack enterprise plan", "product analytics enterprise pricing"],
    "/pricing/startups": ["product analytics for startups free", "thrivestack startup discount"],
    "/integrations/segment": ["thrivestack segment integration", "send segment data to thrivestack"],
    "/integrations/snowflake": ["product analytics that reads from snowflake", "thrivestack snowflake setup"],
    "/integrations/hubspot": ["sync product usage to hubspot", "thrivestack hubspot integration"],
    "/integrations/salesforce": ["push plg signals to salesforce", "thrivestack salesforce sync"],
    "/integrations/stripe": ["connect stripe revenue to product analytics"],
    "/integrations/webhooks": ["thrivestack webhooks docs"],
    // §5.3 — 12 prompts, $7/session. Heavily cited, earns almost nothing.
    "/blog/what-is-a-cdp": [
        "what is a cdp",
        "customer data platform explained",
        "cdp vs dmp difference",
        "do i need a customer data platform",
        "what does a cdp actually do",
        "cdp definition marketing",
        "is segment a cdp",
        "cdp vs data warehouse",
        "customer data platform for small business",
        "how does a cdp work",
        "cdp benefits and drawbacks",
        "difference between cdp and crm"
    ],
    "/blog/product-led-growth-metrics": [
        "plg metrics to track",
        "product led growth kpis",
        "what is a good activation rate",
        "how to measure product led growth",
        "plg north star metrics"
    ],
    "/blog/plg-vs-sales-led": ["product led vs sales led growth", "when to switch from plg to sales led", "plg vs sales led saas"],
    "/blog/activation-rate-benchmarks": ["average saas activation rate", "activation rate benchmark b2b"],
    "/blog/self-serve-onboarding": ["saas self serve onboarding best practices"],
    "/blog/free-trial-vs-freemium": ["free trial or freemium for b2b saas", "freemium vs free trial conversion rates"],
    "/blog/north-star-metric": ["how to pick a north star metric"],
    "/glossary/product-qualified-lead": ["what is a pql", "product qualified lead definition", "pql vs mql"],
    "/glossary/time-to-value": ["what is time to value", "how to reduce time to value saas"],
    "/glossary/activation": ["what is activation in saas"],
    "/glossary/customer-data-platform": ["customer data platform meaning", "cdp glossary"],
    "/": ["what is thrivestack", "thrivestack review"],
    "/product/analytics": ["thrivestack product analytics features", "account level product analytics"],
    "/product/attribution": ["multi touch attribution for b2b saas", "thrivestack attribution", "attribution without a crm"],
    "/demo": ["book a thrivestack demo"],
    "/customers/fivex": ["thrivestack case study"],
    "/docs/quickstart": ["thrivestack setup guide", "how to install thrivestack"]
};

// ── Types ────────────────────────────────────────────────────────────────────

export interface DailyPoint {
    /** ISO yyyy-mm-dd */
    date: string;
    ai: number;
    direct: number;
    organic: number;
}

export interface TrendSeries {
    /** Week-start label, e.g. "Jun 2". */
    label: string;
    date: string;
    ai: number;
    direct: number;
    organic: number;
}

export interface PageRow {
    path: string;
    title: string;
    type: PageTypeId;
    aiSessions: number;
    aiConversions: number;
    /** aiConversions / aiSessions */
    cvr: number;
    /** null when the page fails the confidence check — UI renders a muted dash. */
    valuePerSession: number | null;
    /** Always populated; falls back to the site average for low-confidence rows. */
    revenue: number;
    /** Conversions across ALL channels — the sample the rate is estimated from. */
    totalConversions: number;
    lowConfidence: boolean;
    citedPromptCount: number;
    prompts: string[];
}

export interface PageTypeRow {
    type: PageTypeId;
    label: string;
    aiSessions: number;
    aiConversions: number;
    totalConversions: number;
    revenue: number;
    valuePerSession: number | null;
    lowConfidence: boolean;
}

export interface ChannelTotals {
    sessions: number;
    conversions: number;
    revenue: number;
    valuePerSession: number;
}

export interface AttributionDataset {
    windowDays: number;
    daily: {
        sessions: DailyPoint[];
        conversions: DailyPoint[];
        revenue: DailyPoint[];
    };
    weekly: Record<TrendMetric, TrendSeries[]>;
    current: Record<ChannelId, ChannelTotals>;
    previous: Record<ChannelId, ChannelTotals>;
    delta: { sessions: number; conversions: number; revenue: number; revPerSession: number };
    pages: PageRow[];
    pageTypes: PageTypeRow[];
    /** Share of total sessions that resolve to the AI channel. */
    aiShareOfSessions: number;
    sourceMix: { name: string; share: number; sessions: number; color: string }[];
}

// ── Generation ───────────────────────────────────────────────────────────────

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function isoDate(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Daily session weights for one channel across `days` days.
 * `startFactor`/`endFactor` set the trend; the seed keeps the jitter stable.
 */
function dailyWeights(days: number, dates: Date[], startFactor: number, endFactor: number, seed: number): number[] {
    const rand = mulberry32(seed);
    return dates.slice(0, days).map((d, i) => {
        const trend = startFactor + (endFactor - startFactor) * (days === 1 ? 0 : i / (days - 1));
        const jitter = 0.9 + rand() * 0.2;
        return trend * seasonality(d.getDay()) * jitter;
    });
}

function buildDates(endDate: Date, days: number): Date[] {
    const out: Date[] = [];
    for (let i = days - 1; i >= 0; i--) {
        const d = new Date(endDate);
        d.setDate(d.getDate() - i);
        out.push(d);
    }
    return out;
}

/**
 * Weekly buckets, aligned to the END of the window so the most recent bucket is
 * always a complete week. A trailing partial week would read as a collapse in
 * traffic and make the growth trend unreadable; the leading remainder is dropped.
 */
function toWeekly(daily: DailyPoint[]): TrendSeries[] {
    const offset = daily.length % 7;
    const buckets: TrendSeries[] = [];
    for (let i = offset; i < daily.length; i += 7) {
        const slice = daily.slice(i, i + 7);
        const first = slice[0];
        const [, m, d] = first.date.split("-");
        buckets.push({
            label: `${MONTHS[Number(m) - 1]} ${Number(d)}`,
            date: first.date,
            ai: slice.reduce((a, p) => a + p.ai, 0),
            direct: slice.reduce((a, p) => a + p.direct, 0),
            organic: slice.reduce((a, p) => a + p.organic, 0)
        });
    }
    return buckets;
}

function buildDataset(endDate: Date): AttributionDataset {
    const dates = buildDates(endDate, WINDOW_DAYS);

    // ── Daily sessions per channel, normalised to the calibration targets ────
    // AI climbs ~41% across the window; Organic and Direct are effectively flat.
    const aiSessionsDaily = allocate(dailyWeights(WINDOW_DAYS, dates, 0.83, 1.17, 0x5eed01), TARGET.aiSessions);
    const organicSessionsDaily = allocate(dailyWeights(WINDOW_DAYS, dates, 1.01, 0.99, 0x5eed02), TARGET.organicSessions);
    const directSessionsDaily = allocate(dailyWeights(WINDOW_DAYS, dates, 0.98, 1.02, 0x5eed03), TARGET.directSessions);

    const sessions: DailyPoint[] = dates.map((d, i) => ({
        date: isoDate(d),
        ai: aiSessionsDaily[i],
        direct: directSessionsDaily[i],
        organic: organicSessionsDaily[i]
    }));

    // Conversions follow sessions, with AI converting ~3x Organic.
    const organicConversions = Math.round((TARGET.organicSessions * CHANNEL_VALUE_PER_SESSION.organic) / VALUE_PER_CONVERSION.lead);
    const directConversions = Math.round((TARGET.directSessions * CHANNEL_VALUE_PER_SESSION.direct) / VALUE_PER_CONVERSION.lead);

    const aiConvDaily = allocate(aiSessionsDaily.map((s, i) => s * (1 + (i / WINDOW_DAYS) * 0.05)), TARGET.aiConversions);
    const organicConvDaily = allocate(organicSessionsDaily, organicConversions);
    const directConvDaily = allocate(directSessionsDaily, directConversions);

    const conversions: DailyPoint[] = dates.map((d, i) => ({
        date: isoDate(d),
        ai: aiConvDaily[i],
        direct: directConvDaily[i],
        organic: organicConvDaily[i]
    }));

    const aiRevDaily = allocate(aiSessionsDaily, TARGET.aiRevenue);
    const organicRevDaily = allocate(organicSessionsDaily, Math.round(TARGET.organicSessions * CHANNEL_VALUE_PER_SESSION.organic));
    const directRevDaily = allocate(directSessionsDaily, Math.round(TARGET.directSessions * CHANNEL_VALUE_PER_SESSION.direct));

    const revenue: DailyPoint[] = dates.map((d, i) => ({
        date: isoDate(d),
        ai: aiRevDaily[i],
        direct: directRevDaily[i],
        organic: organicRevDaily[i]
    }));

    // ── Page-level roll-up ──────────────────────────────────────────────────
    const aiSessionsByPage = allocate(PAGE_SEEDS.map((p) => p.aiWeight), TARGET.aiSessions);

    const totalSessions90d =
        TARGET.aiSessions + TARGET.organicSessions + TARGET.directSessions + TARGET.otherChannelSessions;

    // Full-traffic conversions decide confidence. Blended (non-AI) rate is vps/3,
    // divided by what a conversion on that page is worth.
    const totalConversionsByPage = PAGE_SEEDS.map((p) => {
        const blendedValuePerSession = p.vps / 3;
        const rate = blendedValuePerSession / VALUE_PER_CONVERSION[p.conv];
        return Math.round(p.allSessions * rate);
    });

    const lowConfidenceByPage = totalConversionsByPage.map((c) => c < CONFIDENCE_MIN_CONVERSIONS);

    // Revenue: AI sessions × modeled $/session, calibrated so the column foots to
    // the $376k headline. Low-confidence pages fall back to the site average.
    const revenueWeights = PAGE_SEEDS.map((p, i) => {
        const vps = lowConfidenceByPage[i] ? SITE_AVG_VALUE_PER_SESSION : p.vps;
        return aiSessionsByPage[i] * vps;
    });
    const revenueByPage = allocate(revenueWeights, TARGET.aiRevenue);

    // Observed AI conversions, weighted by the page's own conversion economics.
    const aiConvWeights = PAGE_SEEDS.map((p, i) => revenueByPage[i] / VALUE_PER_CONVERSION[p.conv]);
    const aiConvByPage = allocate(aiConvWeights, TARGET.aiConversions);

    const pages: PageRow[] = PAGE_SEEDS.map((p, i) => {
        const aiSessions = aiSessionsByPage[i];
        const prompts = CITED_PROMPTS[p.path] ?? [];
        return {
            path: p.path,
            title: p.title,
            type: p.type,
            aiSessions,
            aiConversions: aiConvByPage[i],
            cvr: aiSessions > 0 ? aiConvByPage[i] / aiSessions : 0,
            valuePerSession: lowConfidenceByPage[i] ? null : revenueByPage[i] / Math.max(aiSessions, 1),
            revenue: revenueByPage[i],
            totalConversions: totalConversionsByPage[i],
            lowConfidence: lowConfidenceByPage[i],
            citedPromptCount: prompts.length,
            prompts
        };
    }).sort((a, b) => b.revenue - a.revenue);

    const pageTypes: PageTypeRow[] = (Object.keys(PAGE_TYPE_LABELS) as PageTypeId[]).map((type) => {
        const rows = pages.filter((r) => r.type === type);
        const aiSessions = rows.reduce((a, r) => a + r.aiSessions, 0);
        const rev = rows.reduce((a, r) => a + r.revenue, 0);
        const totalConversions = rows.reduce((a, r) => a + r.totalConversions, 0);
        const low = totalConversions < CONFIDENCE_MIN_CONVERSIONS;
        return {
            type,
            label: PAGE_TYPE_LABELS[type],
            aiSessions,
            aiConversions: rows.reduce((a, r) => a + r.aiConversions, 0),
            totalConversions,
            revenue: rev,
            valuePerSession: low || aiSessions === 0 ? null : rev / aiSessions,
            lowConfidence: low
        };
    });

    const totals = (s: number, c: number, r: number): ChannelTotals => ({
        sessions: s,
        conversions: c,
        revenue: r,
        valuePerSession: s > 0 ? r / s : 0
    });

    const current: Record<ChannelId, ChannelTotals> = {
        ai: totals(TARGET.aiSessions, TARGET.aiConversions, TARGET.aiRevenue),
        organic: totals(
            TARGET.organicSessions,
            organicConversions,
            Math.round(TARGET.organicSessions * CHANNEL_VALUE_PER_SESSION.organic)
        ),
        direct: totals(
            TARGET.directSessions,
            directConversions,
            Math.round(TARGET.directSessions * CHANNEL_VALUE_PER_SESSION.direct)
        )
    };

    const previous: Record<ChannelId, ChannelTotals> = {
        ai: totals(TARGET.prevAiSessions, TARGET.prevAiConversions, TARGET.prevAiRevenue),
        organic: totals(
            TARGET.prevOrganicSessions,
            Math.round((TARGET.prevOrganicSessions * CHANNEL_VALUE_PER_SESSION.organic) / VALUE_PER_CONVERSION.lead),
            Math.round(TARGET.prevOrganicSessions * CHANNEL_VALUE_PER_SESSION.organic)
        ),
        direct: totals(
            TARGET.prevDirectSessions,
            Math.round((TARGET.prevDirectSessions * CHANNEL_VALUE_PER_SESSION.direct) / VALUE_PER_CONVERSION.lead),
            Math.round(TARGET.prevDirectSessions * CHANNEL_VALUE_PER_SESSION.direct)
        )
    };

    const pct = (now: number, before: number) => (before === 0 ? 0 : (now - before) / before);

    return {
        windowDays: WINDOW_DAYS,
        daily: { sessions, conversions, revenue },
        weekly: {
            sessions: toWeekly(sessions),
            conversions: toWeekly(conversions),
            revenue: toWeekly(revenue)
        },
        current,
        previous,
        delta: {
            sessions: pct(current.ai.sessions, previous.ai.sessions),
            conversions: pct(current.ai.conversions, previous.ai.conversions),
            revenue: pct(current.ai.revenue, previous.ai.revenue),
            revPerSession: current.ai.valuePerSession / current.organic.valuePerSession
        },
        pages,
        pageTypes,
        aiShareOfSessions: TARGET.aiSessions / totalSessions90d,
        sourceMix: AI_SOURCE_MIX.map((s) => ({ ...s, sessions: Math.round(TARGET.aiSessions * s.share) }))
    };
}

let cached: AttributionDataset | null = null;

/**
 * The demo dataset. Numbers are seeded and identical on every call; only the
 * date labels track today's date.
 */
export function getAttributionDemoData(): AttributionDataset {
    if (!cached) cached = buildDataset(new Date());
    return cached;
}

// ── Wizard fixtures (§4) ─────────────────────────────────────────────────────

export interface KeyEventOption {
    name: string;
    count90d: number;
    defaultSelected: boolean;
    defaultValue: number;
    /**
     * Revenue GA4 already reports against this event over 90 days (sum of the event's
     * value / purchase revenue). Demo-only — drives the "revenue found" amounts. Live
     * GA4 only reports revenue at the property level today, so this has no live source.
     */
    revenue90d: number;
}

export const KEY_EVENTS: KeyEventOption[] = [
    { name: "generate_lead", count90d: 1284, defaultSelected: true, defaultValue: 8000, revenue90d: 128400 },
    { name: "sign_up", count90d: 412, defaultSelected: true, defaultValue: 600, revenue90d: 47600 },
    { name: "newsletter_signup", count90d: 3901, defaultSelected: false, defaultValue: 50, revenue90d: 4100 },
    { name: "file_download", count90d: 2110, defaultSelected: false, defaultValue: 120, revenue90d: 12800 }
];

export interface PageTypeRule {
    id: string;
    type: string;
    patterns: string[];
    /** Pages in the mock corpus that this rule claims. */
    matchedPages: number;
    removable: boolean;
}

/** Total pages in the mock property corpus — drives the live count on step 5. */
export const CORPUS_PAGE_COUNT = 2104;

export const DEFAULT_PAGE_TYPE_RULES: PageTypeRule[] = [
    { id: "comparison", type: "Comparison", patterns: ["/compare/*", "/vs/*", "/alternatives/*"], matchedPages: 186, removable: true },
    { id: "pricing", type: "Pricing", patterns: ["/pricing*"], matchedPages: 9, removable: true },
    { id: "integration", type: "Integration", patterns: ["/integrations/*"], matchedPages: 142, removable: true },
    { id: "content", type: "Content", patterns: ["/blog/*", "/glossary/*"], matchedPages: 1510, removable: true }
];

/** Rough per-pattern corpus size, so editing rules moves the live count. */
export const PATTERN_PAGE_COUNTS: Record<string, number> = {
    "/compare/*": 74,
    "/vs/*": 46,
    "/alternatives/*": 66,
    "/pricing*": 9,
    "/integrations/*": 142,
    "/blog/*": 1188,
    "/glossary/*": 322,
    "/docs/*": 214,
    "/customers/*": 31,
    "/product/*": 12
};

/** Unknown patterns still need a stable, deterministic size. */
export function estimatePatternPageCount(pattern: string): number {
    const known = PATTERN_PAGE_COUNTS[pattern.trim()];
    if (known !== undefined) return known;
    const trimmed = pattern.trim();
    if (!trimmed || trimmed === "/" || trimmed === "*") return 0;
    let hash = 0;
    for (let i = 0; i < trimmed.length; i++) hash = (hash * 31 + trimmed.charCodeAt(i)) >>> 0;
    return 8 + (hash % 120);
}
